import type React from "react";

const SEPARATOR_REGEX = /([-/\\_.\s])/;
const FLEXIBLE_SEPARATOR_PATTERN = "[-/\\\\_\\.\\s]";

function escapeRegexWithFlexibleSeparators(str: string): string {
  const parts = str.split(SEPARATOR_REGEX);

  return parts
    .map((part, index) => {
      if (index % 2 === 1) {
        return FLEXIBLE_SEPARATOR_PATTERN;
      }
      return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    })
    .join("");
}

interface HighlightTextProps {
  text: string;
  mark: string | string[];
  className?: React.HTMLAttributes<HTMLSpanElement>["className"];
  /**
   * If true, splits mark strings into individual words before matching.
   * Useful when you want to highlight any matching words from a longer text.
   * @default false
   */
  tokenize?: boolean;
  /**
   * Minimum word length to consider when tokenize is true.
   * @default 3
   */
  minWordLength?: number;
}

export default function HighlightText({
  text,
  mark,
  className = "bg-emerald-100 px-1",
  tokenize = false,
  minWordLength = 3,
}: HighlightTextProps) {
  if (!mark || (Array.isArray(mark) && mark.length === 0)) {
    return <span>{text}</span>;
  }

  const markArray = Array.isArray(mark) ? mark : [mark];

  let processedMarks = markArray;

  // Tokenize marks if requested
  if (tokenize) {
    processedMarks = markArray.flatMap((m) => {
      return m
        .split(/\s+/)
        .map((word) => word.replace(/[^\w-]/g, "")) // Remove punctuation except hyphens
        .filter((word) => word.length >= minWordLength);
    });
  }

  const escapedMarks = processedMarks
    .filter((m) => m.trim().length > 0)
    .map((m) => escapeRegexWithFlexibleSeparators(m));

  if (escapedMarks.length === 0) {
    return <span>{text}</span>;
  }

  const regex = new RegExp(`(${escapedMarks.join("|")})`, "gi");

  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className={className}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  );
}
